/**
 * Prazos de atendimento (SLA) corporativos (ADR 0061): domínio, validações e o motor de cálculo.
 *
 * Tudo aqui é puro (sem banco): calendário de expediente com fuso IANA, feriados inteiros ou com
 * expediente reduzido, as três unidades de prazo, pausas (C1-B), atrasos e medidas de tempo. As
 * datas entram e saem como instantes em milissegundos UTC; o fuso só vale para ler o expediente.
 */
import { z } from 'zod';
import { localizedText, timezone, uuid } from './schemas.ts';

// ---------------------------------------------------------------------------
// Domínio
// ---------------------------------------------------------------------------

/** Unidades de prazo: horas úteis, dias úteis e períodos consecutivos de 24 horas. */
export const SLA_UNITS = ['business_hours', 'business_days', 'hours_24'] as const;
export type SlaUnit = (typeof SLA_UNITS)[number];

/** Escala corporativa de referência (D2): P1 Crítica, P2 Alta, P3 Normal, P4 Baixa. */
export const SLA_PRIORITIES = ['P1', 'P2', 'P3', 'P4'] as const;
export type SlaPriority = (typeof SLA_PRIORITIES)[number];

/** Relógios de uma atividade. */
export const SLA_CLOCKS = ['triage', 'resolution', 'reopen'] as const;
export type SlaClock = (typeof SLA_CLOCKS)[number];

/** Categorias dos motivos de pausa (D8). */
export const SLA_PAUSE_CATEGORIES = ['requester', 'other_area', 'third_party', 'approval'] as const;
export type SlaPauseCategory = (typeof SLA_PAUSE_CATEGORIES)[number];

/** Calendário com expediente (`business`) ou que não para (`continuous`, 24×7). */
export const CALENDAR_KINDS = ['business', 'continuous'] as const;
export type CalendarKind = (typeof CALENDAR_KINDS)[number];

export const HOLIDAY_KINDS = ['national', 'state', 'municipal', 'company', 'optional'] as const;
export type HolidayKind = (typeof HOLIDAY_KINDS)[number];

/** De qual filial vem o calendário do item: de quem atende (padrão) ou de quem pediu. */
export const CALENDAR_BASES = ['service_branch', 'requester_branch'] as const;
export type CalendarBasis = (typeof CALENDAR_BASES)[number];

export const POLICY_CLASSIFICATIONS = ['internal', 'restricted'] as const;
export type PolicyClassification = (typeof POLICY_CLASSIFICATIONS)[number];

/** Ciclo da versão de política (D5). Na E1 só existem rascunho e descartada. */
export const POLICY_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'returned',
  'rejected',
  'closed',
  'discarded',
] as const;
export type PolicyStatus = (typeof POLICY_STATUSES)[number];

/** Situação de integração da atividade: só documentada ou já medida pelo módulo dono. */
export const ACTIVITY_INTEGRATION = ['documented', 'measured'] as const;
export type ActivityIntegration = (typeof ACTIVITY_INTEGRATION)[number];

/**
 * Equivalência fixa entre a prioridade de cada módulo e a escala corporativa (D2). Os módulos
 * mantêm os seus nomes; o relógio grava as duas. Na TI, a prioridade é impacto × urgência (ADR 0081).
 */
export const PRIORITY_EQUIVALENCE = {
  legal: { critical: 'P1', high: 'P2', normal: 'P3' },
  facilities: { urgent: 'P1', high: 'P2', normal: 'P3', low: 'P4' },
  purchasing: { urgent: 'P1', high: 'P2', normal: 'P3', low: 'P4' },
  it: { urgent: 'P1', high: 'P2', normal: 'P3', low: 'P4' },
} as const satisfies Record<string, Record<string, SlaPriority>>;

export function corporatePriority(module: string, value: string): SlaPriority | null {
  const table = (PRIORITY_EQUIVALENCE as Record<string, Record<string, SlaPriority>>)[module];
  return table?.[value] ?? null;
}

// ---------------------------------------------------------------------------
// Validações das entradas
// ---------------------------------------------------------------------------

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$|^24:00$/;
const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3));

/** Intervalos de um dia, em hora local ("09:00"–"12:00"), em ordem e sem sobreposição. */
export const dayIntervals = z
  .array(z.tuple([z.string().regex(HHMM), z.string().regex(HHMM)]))
  .max(6)
  .refine(
    (list) =>
      list.every(([a, b], i) => {
        if (minutesOf(a) >= minutesOf(b)) return false;
        const prev = list[i - 1];
        return !prev || minutesOf(prev[1]) <= minutesOf(a);
      }),
    'Os intervalos precisam estar em ordem, sem sobreposição, e terminar depois de começar',
  );
export type DayIntervals = z.infer<typeof dayIntervals>;

/** Expediente por dia da semana: "0" é domingo, "6" é sábado. */
export const weeklyHours = z.object({
  '0': dayIntervals,
  '1': dayIntervals,
  '2': dayIntervals,
  '3': dayIntervals,
  '4': dayIntervals,
  '5': dayIntervals,
  '6': dayIntervals,
});
export type WeeklyHours = z.infer<typeof weeklyHours>;

const code = (max: number) =>
  z
    .string()
    .trim()
    .toUpperCase()
    .regex(new RegExp(`^[A-Z0-9][A-Z0-9_.-]{1,${max - 1}}$`), 'Use letras, números, ponto e hífen');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const optionalLocalized = localizedText.nullable().optional();
const reason = z.string().trim().min(5).max(1000);

export const slaProcessInput = z.object({
  code: code(30),
  name: localizedText,
  description: optionalLocalized,
  departmentId: uuid,
  isActive: z.boolean().default(true),
});
export type SlaProcessInput = z.infer<typeof slaProcessInput>;

export const pauseReasonDefinition = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Código em minúsculas, com letras, números e "_"'),
  category: z.enum(SLA_PAUSE_CATEGORIES),
  name: localizedText,
});
export type PauseReasonDefinition = z.infer<typeof pauseReasonDefinition>;

export const slaActivityInput = z
  .object({
    processId: uuid,
    code: code(50),
    name: localizedText,
    description: optionalLocalized,
    recordType: z
      .string()
      .regex(/^[a-z_]+\.[a-z_]+$/)
      .nullable()
      .optional(),
    ownerModule: z
      .string()
      .regex(/^[a-z_]+$/)
      .nullable()
      .optional(),
    startEvent: localizedText,
    endEvent: localizedText,
    clocks: z.array(z.enum(SLA_CLOCKS)).min(1),
    triageRequired: z.boolean().default(false),
    calendarBasis: z.enum(CALENDAR_BASES).default('service_branch'),
    categorySource: z.string().trim().max(100).nullable().optional(),
    pauseReasons: z.array(pauseReasonDefinition).max(30).default([]),
    integrationStatus: z.enum(ACTIVITY_INTEGRATION).default('documented'),
    isActive: z.boolean().default(true),
  })
  .refine((a) => a.clocks.includes('resolution'), {
    message: 'Toda atividade mede a solução',
    path: ['clocks'],
  })
  .refine((a) => !a.triageRequired || a.clocks.includes('triage'), {
    message: 'A triagem obrigatória exige o relógio de triagem',
    path: ['triageRequired'],
  })
  .refine((a) => new Set(a.pauseReasons.map((r) => r.code)).size === a.pauseReasons.length, {
    message: 'Os motivos de pausa não podem repetir o código',
    path: ['pauseReasons'],
  });
export type SlaActivityInput = z.infer<typeof slaActivityInput>;

export const businessCalendarInput = z
  .object({
    code: code(30),
    name: localizedText,
    kind: z.enum(CALENDAR_KINDS).default('business'),
    timeZone: timezone,
    baseCalendarId: uuid.nullable().optional(),
    /** Vazio num calendário local: herda o expediente da base. */
    weeklyHours: weeklyHours.nullable().optional(),
    isActive: z.boolean().default(true),
  })
  .refine((c) => c.kind === 'business' || !c.baseCalendarId, {
    message: 'Um calendário 24×7 não herda de outro',
    path: ['baseCalendarId'],
  })
  .refine((c) => c.kind === 'continuous' || c.baseCalendarId || c.weeklyHours, {
    message: 'Informe o expediente (ou escolha um calendário-base para herdar)',
    path: ['weeklyHours'],
  });
export type BusinessCalendarInput = z.infer<typeof businessCalendarInput>;

export const calendarHoursChange = z.object({
  weeklyHours: weeklyHours.nullable(),
  reason,
});

export const holidayInput = z.object({
  date: isoDate,
  kind: z.enum(HOLIDAY_KINDS),
  name: localizedText,
  /** Nulo: dia inteiro sem expediente. Lista: expediente reduzido (ex.: 09:00–12:00). */
  workingIntervals: dayIntervals.nullable().default(null),
  /** Ponto facultativo só vale se adotado. */
  adopted: z.boolean().default(true),
  reason,
});
export type HolidayInput = z.infer<typeof holidayInput>;

export const holidayRemoval = z.object({ reason });

export const calendarYearCheckInput = z.object({
  year: z.number().int().min(2000).max(2100),
  note: z.string().trim().max(1000).nullable().optional(),
});

export const calendarAssignmentInput = z
  .object({
    scopeType: z.enum(['group', 'legal_entity', 'branch']),
    legalEntityId: uuid.nullable().optional(),
    branchId: uuid.nullable().optional(),
    calendarId: uuid,
  })
  .refine(
    (a) =>
      (a.scopeType === 'group' && !a.legalEntityId && !a.branchId) ||
      (a.scopeType === 'legal_entity' && !!a.legalEntityId && !a.branchId) ||
      (a.scopeType === 'branch' && !!a.branchId && !a.legalEntityId),
    { message: 'Escolha o grupo, uma empresa ou uma filial', path: ['scopeType'] },
  );

const target = z.object({
  amount: z.number().int().min(1).max(10_000),
  unit: z.enum(SLA_UNITS),
});
export type SlaTarget = z.infer<typeof target>;

export const policyVersionInput = z
  .object({
    activityId: uuid,
    legalEntityId: uuid.nullable().optional(),
    classification: z.enum(POLICY_CLASSIFICATIONS).default('internal'),
    validFrom: isoDate,
    validUntil: isoDate.nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    message: 'O fim da vigência não pode ser antes do início',
    path: ['validUntil'],
  });
export type PolicyVersionInput = z.infer<typeof policyVersionInput>;

export const policyTargetInput = z.object({
  priority: z.enum(SLA_PRIORITIES).nullable().optional(),
  branchId: uuid.nullable().optional(),
  category: z.string().trim().min(1).max(60).nullable().optional(),
  triage: target.nullable().optional(),
  resolution: target,
  reopen: target.nullable().optional(),
  calendarId: uuid.nullable().optional(),
  allowedPauseReasons: z
    .array(z.string().regex(/^[a-z][a-z0-9_]{1,39}$/))
    .max(30)
    .default([]),
  longPause: target.nullable().optional(),
  includeConclusiveRefusals: z.boolean().default(false),
});
export type PolicyTargetInput = z.infer<typeof policyTargetInput>;

const instant = z.iso.datetime({ offset: true });
export const slaCalculatorInput = z
  .object({
    calendarId: uuid,
    /** Revisão do calendário (padrão: a atual). */
    revision: z.number().int().min(1).nullable().optional(),
    amount: z.number().int().min(1).max(10_000),
    unit: z.enum(SLA_UNITS),
    start: instant,
    pauses: z
      .array(z.object({ start: instant, end: instant }))
      .max(50)
      .default([]),
    end: instant.nullable().optional(),
  })
  .refine((v) => v.pauses.every((p) => Date.parse(p.end) > Date.parse(p.start)), {
    message: 'Cada pausa termina depois de começar',
    path: ['pauses'],
  });
export type SlaCalculatorInput = z.infer<typeof slaCalculatorInput>;

// ---------------------------------------------------------------------------
// Datas e fusos (sem biblioteca: Intl do Node)
// ---------------------------------------------------------------------------

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
export const SLA_HOUR_MS = HOUR_MS;

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

function wallClock(tz: string, utcMs: number) {
  const parts: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(new Date(utcMs))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  return parts as {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  };
}

/** Diferença entre a hora local e o UTC naquele instante. */
function offsetMs(tz: string, utcMs: number): number {
  const w = wallClock(tz, utcMs);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - (utcMs - (((utcMs % 1000) + 1000) % 1000));
}

/** Instante UTC de uma data e hora locais ("2026-11-09", "09:00"; "24:00" é o fim do dia). */
export function zonedToUtc(date: string, hhmm: string, tz: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const naive = Date.UTC(y, m - 1, d) + minutesOf(hhmm) * 60_000;
  const guess = offsetMs(tz, naive);
  let utc = naive - guess;
  // Perto da troca de horário de verão, o deslocamento do palpite pode não ser o do resultado.
  const actual = offsetMs(tz, utc);
  if (actual !== guess) utc = naive - actual;
  return utc;
}

/** Data local (AAAA-MM-DD) de um instante, no fuso. */
export function localDateOf(utcMs: number, tz: string): string {
  const w = wallClock(tz, utcMs);
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d) + days * DAY_MS).toISOString().slice(0, 10);
}

function weekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

// ---------------------------------------------------------------------------
// Calendário
// ---------------------------------------------------------------------------

export interface CalendarHoliday {
  date: string;
  /** Nulo: sem expediente. Lista: expediente reduzido nesse dia. */
  workingIntervals: DayIntervals | null;
}

/** O calendário numa revisão: o que o cálculo precisa (base e local já juntos). */
export interface CalendarSnapshot {
  kind: CalendarKind;
  timeZone: string;
  weeklyHours: WeeklyHours;
  holidays: readonly CalendarHoliday[];
}

export const NO_HOURS: WeeklyHours = {
  '0': [],
  '1': [],
  '2': [],
  '3': [],
  '4': [],
  '5': [],
  '6': [],
};

/**
 * Junta a base e o local (um nível de herança): o local usa o próprio expediente ou o da base; os
 * feriados são os dois, e o do local prevalece na mesma data.
 */
export function mergeCalendars(
  local: {
    kind: CalendarKind;
    timeZone: string;
    weeklyHours: WeeklyHours | null;
    holidays: readonly CalendarHoliday[];
  },
  base: { weeklyHours: WeeklyHours | null; holidays: readonly CalendarHoliday[] } | null,
): CalendarSnapshot {
  const byDate = new Map<string, CalendarHoliday>();
  for (const h of base?.holidays ?? []) byDate.set(h.date, h);
  for (const h of local.holidays) byDate.set(h.date, h);
  return {
    kind: local.kind,
    timeZone: local.timeZone,
    weeklyHours: local.weeklyHours ?? base?.weeklyHours ?? NO_HOURS,
    holidays: [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date)),
  };
}

type Interval = readonly [number, number];

const holidayIndex = new WeakMap<CalendarSnapshot, Map<string, CalendarHoliday>>();
function holidayOf(cal: CalendarSnapshot, date: string): CalendarHoliday | undefined {
  let idx = holidayIndex.get(cal);
  if (!idx) {
    idx = new Map(cal.holidays.map((h) => [h.date, h]));
    holidayIndex.set(cal, idx);
  }
  return idx.get(date);
}

/** Intervalos de expediente de uma data local, como instantes UTC. */
export function workingIntervalsOn(cal: CalendarSnapshot, date: string): Interval[] {
  const tz = cal.timeZone;
  if (cal.kind === 'continuous')
    return [[zonedToUtc(date, '00:00', tz), zonedToUtc(date, '24:00', tz)]];
  const holiday = holidayOf(cal, date);
  const local = holiday
    ? (holiday.workingIntervals ?? [])
    : cal.weeklyHours[String(weekday(date)) as keyof WeeklyHours];
  return local.map(([a, b]) => [zonedToUtc(date, a, tz), zonedToUtc(date, b, tz)] as const);
}

/** Limite de busca: um calendário sem expediente nenhum não pode travar o cálculo. */
const MAX_DAYS = 3700;
export class CalendarWithoutWorkingTime extends Error {
  constructor() {
    super('O calendário não tem expediente suficiente para calcular o prazo');
  }
}

/** O N-ésimo dia com expediente depois de `date` (zero devolve o próprio `date`). */
export function addWorkingDays(cal: CalendarSnapshot, date: string, days: number): string {
  let current = date;
  let left = days;
  for (let i = 0; left > 0; i++) {
    if (i >= MAX_DAYS) throw new CalendarWithoutWorkingTime();
    current = addDays(current, 1);
    if (workingIntervalsOn(cal, current).length > 0) left--;
  }
  return current;
}

/** Dias com expediente depois de `from` até `to`, inclusive. */
export function workingDaysBetween(cal: CalendarSnapshot, from: string, to: string): number {
  let count = 0;
  for (let date = addDays(from, 1), i = 0; date <= to && i < MAX_DAYS; i++) {
    if (workingIntervalsOn(cal, date).length > 0) count++;
    date = addDays(date, 1);
  }
  return count;
}

/** O instante em que se completa `amountMs` de expediente a partir de `fromMs` (C3: fecha no limite). */
export function addWorkingTime(cal: CalendarSnapshot, fromMs: number, amountMs: number): number {
  if (amountMs <= 0) return fromMs;
  let remaining = amountMs;
  let date = localDateOf(fromMs, cal.timeZone);
  for (let i = 0; i < MAX_DAYS; i++) {
    for (const [a, b] of workingIntervalsOn(cal, date)) {
      const s = Math.max(a, fromMs);
      if (s >= b) continue;
      const available = b - s;
      if (remaining <= available) return s + remaining;
      remaining -= available;
    }
    date = addDays(date, 1);
  }
  throw new CalendarWithoutWorkingTime();
}

/** Expediente entre dois instantes. */
export function workingTimeBetween(cal: CalendarSnapshot, fromMs: number, toMs: number): number {
  if (toMs <= fromMs) return 0;
  let total = 0;
  let date = localDateOf(fromMs, cal.timeZone);
  const last = localDateOf(toMs, cal.timeZone);
  for (let i = 0; i < MAX_DAYS && date <= last; i++) {
    for (const [a, b] of workingIntervalsOn(cal, date)) {
      total += Math.max(0, Math.min(b, toMs) - Math.max(a, fromMs));
    }
    date = addDays(date, 1);
  }
  return total;
}

/** Primeiro instante de expediente a partir de `fromMs`. */
export function nextWorkingInstant(cal: CalendarSnapshot, fromMs: number): number {
  let date = localDateOf(fromMs, cal.timeZone);
  for (let i = 0; i < MAX_DAYS; i++) {
    for (const [a, b] of workingIntervalsOn(cal, date)) {
      if (b > fromMs) return Math.max(a, fromMs);
    }
    date = addDays(date, 1);
  }
  throw new CalendarWithoutWorkingTime();
}

// ---------------------------------------------------------------------------
// Prazos (regras C1-B a C7)
// ---------------------------------------------------------------------------

export interface Pause {
  start: number;
  end: number;
}

export interface DueResult {
  /** Onde a contagem começa (C2: em dias úteis, no primeiro dia útil depois da abertura). */
  countStart: number;
  /** Vencimento sem pausas. */
  originalDue: number;
  /** Vencimento depois das pausas (C1-B). */
  due: number;
}

/** Vencimento inicial de uma meta, sem pausas. */
export function initialDue(cal: CalendarSnapshot, startMs: number, target: SlaTarget) {
  if (target.unit === 'hours_24') {
    return { countStart: startMs, due: startMs + target.amount * 24 * HOUR_MS };
  }
  if (target.unit === 'business_hours') {
    return {
      countStart: nextWorkingInstant(cal, startMs),
      due: addWorkingTime(cal, startMs, target.amount * HOUR_MS),
    };
  }
  // Dias úteis: a data da abertura não conta; vence no fim do expediente do enésimo dia útil.
  let date = localDateOf(startMs, cal.timeZone);
  let counted = 0;
  let countStart: number | null = null;
  for (let i = 0; i < MAX_DAYS; i++) {
    date = addDays(date, 1);
    const day = workingIntervalsOn(cal, date);
    if (day.length === 0) continue;
    countStart ??= day[0]![0];
    counted += 1;
    if (counted === target.amount) return { countStart, due: day[day.length - 1]![1] };
  }
  throw new CalendarWithoutWorkingTime();
}

/** Tempo da pausa que conta, na base da unidade (expediente ou tempo real). */
function pausedTime(cal: CalendarSnapshot, unit: SlaUnit, from: number, to: number): number {
  if (to <= from) return 0;
  return unit === 'hours_24' ? to - from : workingTimeBetween(cal, from, to);
}

/**
 * Vencimento com pausas (C1-B): cada pausa autorizada que começa até o vencimento o empurra
 * exatamente pelo tempo pausado na unidade (expediente depois do início da contagem; tempo real nos
 * períodos de 24h). Pausa que começa depois do vencimento não o muda: o atraso não se apaga.
 */
export function dueWithPauses(
  cal: CalendarSnapshot,
  startMs: number,
  target: SlaTarget,
  pauses: readonly Pause[],
): DueResult {
  const first = initialDue(cal, startMs, target);
  let due = first.due;
  for (const p of [...pauses].sort((a, b) => a.start - b.start)) {
    if (p.start > due) continue;
    const ext = pausedTime(cal, target.unit, Math.max(p.start, first.countStart), p.end);
    if (ext <= 0) continue;
    due = target.unit === 'hours_24' ? due + ext : addWorkingTime(cal, due, ext);
  }
  return { countStart: first.countStart, originalDue: first.due, due };
}

/** C4: cumprir no próprio instante do vencimento é cumprir. */
export function isMet(outcomeMs: number, dueMs: number): boolean {
  return outcomeMs <= dueMs;
}

/** O primeiro instante descumprido: um segundo depois do vencimento. */
export function breachInstant(dueMs: number): number {
  return dueMs + 1000;
}

export interface Lateness {
  /** Sem descontos. */
  gross: number;
  /** Descontadas as pausas autorizadas que começaram depois do vencimento. */
  net: number;
}

/** Atraso bruto e líquido entre o vencimento e o desfecho (ou agora), na base da unidade. */
export function lateness(
  cal: CalendarSnapshot,
  unit: SlaUnit,
  dueMs: number,
  endMs: number,
  pauses: readonly Pause[],
): Lateness {
  if (endMs <= dueMs) return { gross: 0, net: 0 };
  const gross = pausedTime(cal, unit, dueMs, endMs);
  let after = 0;
  for (const p of pauses) {
    if (p.start <= dueMs) continue;
    after += pausedTime(cal, unit, p.start, Math.min(p.end, endMs));
  }
  return { gross, net: Math.max(0, gross - after) };
}

export interface TimeMeasures {
  /** Tempo real do início ao desfecho. */
  elapsed: number;
  /** Expediente do calendário, sem descontar pausas (em 24h, igual ao corrido). */
  grossUseful: number;
  /** Expediente menos as pausas autorizadas. */
  netUseful: number;
}

/** As três medidas de tempo de um item (ADR 0061, regra 8). */
export function timeMeasures(
  cal: CalendarSnapshot,
  unit: SlaUnit,
  startMs: number,
  endMs: number,
  pauses: readonly Pause[],
): TimeMeasures {
  const elapsed = Math.max(0, endMs - startMs);
  const grossUseful = unit === 'hours_24' ? elapsed : workingTimeBetween(cal, startMs, endMs);
  let paused = 0;
  for (const p of pauses) {
    paused += pausedTime(cal, unit, Math.max(p.start, startMs), Math.min(p.end, endMs));
  }
  return { elapsed, grossUseful, netUseful: Math.max(0, grossUseful - paused) };
}

/**
 * Um tempo de expediente em "dias úteis inteiros + horas", para mostrar na unidade contratada:
 * conta os dias cujo expediente inteiro está entre os dois instantes; o resto fica em horas.
 */
export function splitWorkingDays(cal: CalendarSnapshot, fromMs: number, toMs: number) {
  let days = 0;
  let rest = 0;
  if (toMs <= fromMs) return { days, rest };
  let date = localDateOf(fromMs, cal.timeZone);
  const last = localDateOf(toMs, cal.timeZone);
  for (let i = 0; i < MAX_DAYS && date <= last; i++) {
    const day = workingIntervalsOn(cal, date);
    const full = day.reduce((s, [a, b]) => s + (b - a), 0);
    const inside = day.reduce(
      (s, [a, b]) => s + Math.max(0, Math.min(b, toMs) - Math.max(a, fromMs)),
      0,
    );
    if (full > 0 && inside === full) days += 1;
    else rest += inside;
    date = addDays(date, 1);
  }
  return { days, rest };
}

/** Resultado da calculadora (simulação não oficial: nada é gravado). */
export interface SlaCalculation {
  nonOfficial: true;
  countStart: string;
  originalDue: string;
  due: string;
  breachAt: string;
  outcome: null | {
    met: boolean;
    measures: TimeMeasures;
    lateness: Lateness;
    latenessDays: { days: number; rest: number } | null;
  };
}

/** A calculadora: vencimento, pausas e, se houver fim, cumprimento, medidas e atraso. */
export function calculateSla(
  cal: CalendarSnapshot,
  input: {
    amount: number;
    unit: SlaUnit;
    start: string;
    pauses: { start: string; end: string }[];
    end?: string | null;
  },
): SlaCalculation {
  const start = Date.parse(input.start);
  const pauses = input.pauses.map((p) => ({ start: Date.parse(p.start), end: Date.parse(p.end) }));
  const target = { amount: input.amount, unit: input.unit };
  const r = dueWithPauses(cal, start, target, pauses);
  const iso = (ms: number) => new Date(ms).toISOString();
  let outcome: SlaCalculation['outcome'] = null;
  if (input.end) {
    const end = Date.parse(input.end);
    const late = lateness(cal, input.unit, r.due, end, pauses);
    outcome = {
      met: isMet(end, r.due),
      measures: timeMeasures(cal, input.unit, start, end, pauses),
      lateness: late,
      latenessDays:
        input.unit === 'business_days' && late.gross > 0 ? splitWorkingDays(cal, r.due, end) : null,
    };
  }
  return {
    nonOfficial: true,
    countStart: iso(r.countStart),
    originalDue: iso(r.originalDue),
    due: iso(r.due),
    breachAt: iso(breachInstant(r.due)),
    outcome,
  };
}

// ---------------------------------------------------------------------------
// Políticas: regra mais específica, vigências e calendário aplicável
// ---------------------------------------------------------------------------

export interface TargetCandidate {
  id: string;
  /** Empresa da versão (nula = grupo). */
  legalEntityId: string | null;
  branchId: string | null;
  category: string | null;
  priority: SlaPriority | null;
}

/**
 * A linha que vale para o item: só as compatíveis e, entre elas, a mais específica (categoria >
 * filial > empresa > grupo; prioridade exata > qualquer).
 */
export function resolveTarget<T extends TargetCandidate>(
  candidates: readonly T[],
  item: {
    legalEntityId: string;
    branchId: string | null;
    category: string | null;
    priority: SlaPriority | null;
  },
): T | null {
  let best: T | null = null;
  let bestScore = -1;
  for (const c of candidates) {
    if (c.legalEntityId && c.legalEntityId !== item.legalEntityId) continue;
    if (c.branchId && c.branchId !== item.branchId) continue;
    if (c.category && c.category !== item.category) continue;
    if (c.priority && c.priority !== item.priority) continue;
    const score =
      (c.category ? 8 : 0) +
      (c.branchId ? 4 : 0) +
      (c.legalEntityId ? 2 : 0) +
      (c.priority ? 1 : 0);
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  return best;
}

/** Duas vigências (datas inclusivas; sem fim = em aberto) se sobrepõem? */
export function vigenciesOverlap(
  a: { validFrom: string; validUntil: string | null },
  b: { validFrom: string; validUntil: string | null },
): boolean {
  const aEnd = a.validUntil ?? '9999-12-31';
  const bEnd = b.validUntil ?? '9999-12-31';
  return a.validFrom <= bEnd && b.validFrom <= aEnd;
}

export interface CalendarAssignmentRow {
  scopeType: 'group' | 'legal_entity' | 'branch';
  legalEntityId: string | null;
  branchId: string | null;
  calendarId: string;
}

/** Calendário aplicável: o da filial; senão, o da empresa; senão, o do grupo; senão, nenhum. */
export function resolveCalendarId(
  assignments: readonly CalendarAssignmentRow[],
  item: { legalEntityId: string; branchId: string | null },
): string | null {
  const byBranch = item.branchId
    ? assignments.find((a) => a.scopeType === 'branch' && a.branchId === item.branchId)
    : undefined;
  if (byBranch) return byBranch.calendarId;
  const byEntity = assignments.find(
    (a) => a.scopeType === 'legal_entity' && a.legalEntityId === item.legalEntityId,
  );
  if (byEntity) return byEntity.calendarId;
  return assignments.find((a) => a.scopeType === 'group')?.calendarId ?? null;
}
